import React, { useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { IconButton } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydButton from '../../../../../commonDesign/Button'
import EmptySection from '../../../../../atoms/EmptySection'
import Icon from '../../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../../constants'

const ITEM_TYPE = 'COMPONENT_ITEM'

function DraggableItem ({ code, data, index, isSelected, onSelect, onRemove, onMoveItem }) {
  const ref = useRef(null)

  const [{ handlerId }, drop] = useDrop({
    accept: ITEM_TYPE,
    collect (monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover (item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const rect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (rect.bottom - rect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - rect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      onMoveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: () => ({ id: code, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      role='option'
      tabIndex={0}
      aria-selected={isSelected}
      data-handler-id={handlerId}
      onClick={() => onSelect(code)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(code)
        }
      }}
      style={{
        padding: '8px 12px',
        marginBottom: '8px',
        borderRadius: '4px',
        cursor: 'pointer',
        color: isSelected ? 'var(--color-contrast-primary-500)' : 'inherit',
        backgroundColor: isSelected ? 'var(--color-primary-500)' : 'transparent',
        border: isSelected ? '1px solid var(--color-primary-500)' : '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        opacity: isDragging ? 0.4 : 1,
        transition: 'all 0.2s'
      }}
    >
      <div style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
        <Icon name={ICON_NAMES.dragIndicator} customSize='1.2rem' />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, marginBottom: '2px' }}>{data.displayName}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--color-neutral-400)' }}>
          {data.componentType} &bull; {code}
        </div>
      </div>
      <IconButton
        size='small'
        onClick={(e) => {
          e.stopPropagation()
          onRemove(code)
        }}
      >
        <DeleteIcon fontSize='small' />
      </IconButton>
    </div>
  )
}

DraggableItem.propTypes = {
  code: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMoveItem: PropTypes.func.isRequired
}

function ComponentListSidebar ({
  presentations,
  selectedCode,
  onSelect,
  onAdd,
  onRemove,
  onReorder
}) {
  const sortedEntries = useMemo(() => {
    return Object.entries(presentations || {})
      .sort(([, a], [, b]) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
  }, [presentations])

  return (
    <div style={{
      borderRight: '1px solid var(--color-neutral-600)',
      paddingRight: '16px',
      minHeight: '400px'
    }}
    >
      <div style={{ marginBottom: '12px' }}>
        <SydButton
          variant='primary'
          size='sm'
          icon='add'
          onClick={onAdd}
          style={{ width: '100%' }}
        >
          Add Component
        </SydButton>
      </div>

      {sortedEntries.length === 0 ? (
        <div style={{ paddingTop: '32px' }}>
          <EmptySection
            title='No Components'
            description='Click Add Component to get started'
            emptyDescription
          />
        </div>
      ) : (
        <DndProvider backend={HTML5Backend}>
          <div role='listbox' aria-label='Components'>
            {sortedEntries.map(([code, data], idx) => (
              <DraggableItem
                key={code}
                code={code}
                data={data}
                index={idx}
                isSelected={selectedCode === code}
                onSelect={onSelect}
                onRemove={onRemove}
                onMoveItem={onReorder}
              />
            ))}
          </div>
        </DndProvider>
      )}
    </div>
  )
}

ComponentListSidebar.propTypes = {
  presentations: PropTypes.object.isRequired,
  selectedCode: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired
}

export default ComponentListSidebar
